import styled from '@emotion/styled';
import { AnimatePresence, motion } from 'motion/react';
import { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import { InputWrapper } from '~/modules/Elements/AKUI/InputWrapper';
import { typography } from '~/modules/Elements/cssMixins';
import styles from '~/modules/GameEngine/Drawing/styles';

interface Props extends PropsWithChildren, ComponentProps<typeof ConfigurationPosition> {
  focused?: boolean;
  disabled?: boolean;
  label: ReactNode;
  value: string | number | undefined | null;
  displayValue?: ReactNode;
  info?: ReactNode;
  onClick?: () => void;
}

export const Switcher = ({
  focused,
  disabled = false,
  label,
  value,
  displayValue,
  onClick,
  info,
  children,
  className = '',
  ...restProps
}: Props) => (
  <InputWrapper info={info}>
    <ConfigurationPosition
      data-focused={focused}
      onClick={onClick}
      data-disabled={disabled}
      {...restProps}
      className={`${className} shadow-focusable rounded-md`}>
      {/* label */}
      <ConfigLabel>{label ? <>{label}:</> : ''}</ConfigLabel>

      {/* value */}
      <AnimatePresence mode="wait">
        <ConfigValue
          key={String(value ?? '')}
          initial={{ opacity: 0, translateY: 12 }}
          animate={{ opacity: 1, translateY: 0 }}
          exit={{ opacity: 0, translateY: -12 }}>
          {displayValue ?? value}
        </ConfigValue>
      </AnimatePresence>

      {children ?? null}
    </ConfigurationPosition>
  </InputWrapper>
);

const ConfigurationPosition = styled.span<{ disabled?: boolean }>`
  background: var(--walky-surface-raised);
  width: 100%;
  ${typography};

  font-size: clamp(1.05rem, 2vw, 1.45rem);
  cursor: pointer;
  padding: 0.75rem 1rem;
  border: 1px solid var(--walky-border);
  border-radius: 8px;
  color: var(--walky-ink);

  /* ✅ layout estable (label izq / value dcha) */
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;

  /* ✅ imprescindible para truncado correcto en flex */
  min-width: 0;
  overflow: hidden;

  &[data-expanded='true'] {
    font-size: clamp(1.35rem, 3vw, 2.2rem);
  }

  &[data-disabled='true'] {
    cursor: default;
  }

  &[data-focused='true'][data-disabled='false'],
  &:hover {
    border-color: var(--walky-orange);
    box-shadow: 0 0 0 2px rgba(244, 90, 10, 0.2);
  }

  /* ✅ Móvil: bajar un pelín para que no se coma el panel */
  @media (max-width: 520px) {
    font-size: 1.05rem;
    padding: 0.6rem;
  }
`;

const ConfigLabel = styled.span`
  flex: 0 0 auto;
  opacity: 0.95;

  @media (max-width: 520px) {
    /* si el label es largo, que tampoco rompa */
    max-width: 55%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ConfigValue = styled(motion.span)`
  flex: 1 1 auto;
  min-width: 0; /* ✅ CRÍTICO */

  color: ${styles.colors.text.active};
  text-align: right;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* ✅ evita que el value “toque” el borde */
  padding-left: 0.75rem;

  @media (max-width: 520px) {
    padding-left: 0.5rem;
  }
`;
